<template>
  <div
    id="sidebar"
    class="w-full h-12 md:fixed top-0 left-0 flex justify-between items-center md:flex-col md:h-screen bg-white"
  >
    <em @click="sidebar" class="fa-solid fa-bars md:hidden"></em>
    <router-link :to="{ name: 'Home' }" class="logo-container mt-3">
      <img 
        id="logo" 
        :src="currentLogo || locationLogo || defaultLogo" 
        :alt="locationName" 
        class="logo-image"
        @load="handleImageLoad"
        ref="logoImage"
        @error="handleImageError"
        v-show="!isLoading"
      />
      <em v-if="isLoading" class="fa fa-spinner fa-spin text-gray-400 text-2xl"></em>
    </router-link>
    <ul
      v-if="data && showSideBarItems"
      class="absolute md:static left-0 top-0 pt-8 md:pt-0 flex flex-col justify-between overflow-auto h-screen mt-8 md:mt-4 mb-auto md:h-full bg-white w-1/2 md:w-full z-10"
    >
      <li>
        <SideBarItem
          v-for="item in data.topRoutes"
          :data="item.children"
          :key="item"
          :label="item.title"
          :icon="item.icon"
          :depth="0"
          :route="item.route"
          :external="item.external"
          :link="item.link"
          class="mt-1"
        />
      </li>
      <li class="mt-2">
        <SideBarItem
          v-for="item in data.bottomRoutes"
          :data="item.children"
          :key="item.title"
          :label="item.title"
          :icon="item.icon"
          :depth="0"
          :route="item.route"
          :external="item.external"
          :link="item.link"
          class="logout-button mb-16"
        />
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useStore } from "vuex";
import SideBarItem from "@/components/general/SideBarItem.vue";

const showSideBarItems = ref(true);
const store = useStore();
const logoImage = ref(null);
const imageDimensions = ref({ width: 0, height: 0 });
const isLoading = ref(true);

defineProps({
  data: {
    type: Object,
    default: null,
  },
});

const defaultLogo = ref(require("@/assets/brain-logo.png"));
const currentLogo = ref(null);

const locationLogo = computed(() => store.getters["location/getLocationLogo"]);
const locationName = computed(() => store.getters["location/getLocationName"]);

const windowSize = ref(window.innerWidth);
onMounted(() => {
  window.addEventListener("resize", () => {
    windowSize.value = window.innerWidth;
  });
  if (windowSize.value < 768) {
    showSideBarItems.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", () => {
    windowSize.value = window.innerWidth;
  });
});

function sidebar() {
  showSideBarItems.value = !showSideBarItems.value;
}

function handleImageLoad(event) {
  isLoading.value = false;
  const img = event.target;
  const maxWidth = 200;
  const maxHeight = 100;
  
  const aspectRatio = img.naturalWidth / img.naturalHeight;
  
  if (aspectRatio > 1) {
    imageDimensions.value = {
      width: maxWidth,
      height: maxWidth / aspectRatio
    };
  } else {
    imageDimensions.value = {
      width: maxHeight * aspectRatio,
      height: maxHeight
    };
  }
}

function handleImageError() {
  currentLogo.value = defaultLogo.value;
  isLoading.value = false;
}
</script>

<style scoped>
.logo-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.logo-image {
  object-fit: contain;
  max-width: 100%;
  height: auto;
}

@media (min-width: 768px) {
  .logo-container {
    width: 100%;
    padding: 1.2rem;
  }

  .logo-image {
    width: v-bind('imageDimensions.width + "px"');
    height: v-bind('imageDimensions.height + "px"');
  }

  #sidebar {
    width: 286px;
  }
}
</style>
